import { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { 
  Settings, 
  Brain, 
  Shield, 
  FileText,
  Search
} from 'lucide-react'
import { adminApi } from '@/lib/api'

interface PlatformSetting {
  key: string
  value: string | null
  description: string | null
  createdAt: string
  updatedAt: string
}

interface LLMModel {
  id: string
  name: string
  provider: string
  description: string
}

export function PlatformSettingsPage() {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [settings, setSettings] = useState<Record<string, PlatformSetting>>({})
  const [availableModels, setAvailableModels] = useState<LLMModel[]>([])
  const [availableToolModels, setAvailableToolModels] = useState<LLMModel[]>([])
  const [defaultModel, setDefaultModel] = useState('')
  const [defaultToolModel, setDefaultToolModel] = useState('')
  const [globalSystemPromptValue, setGlobalSystemPromptValue] = useState('')
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)

  // Load platform settings
  const loadSettings = async () => {
    try {
      setLoading(true)
      const [settingsData, llmData] = await Promise.all([
        adminApi.getPlatformSettings(),
        adminApi.getLLMModelSettings()
      ])
      
      // Convert array to keyed object for easier access
      const settingsMap = settingsData.reduce((acc: Record<string, PlatformSetting>, setting: PlatformSetting) => {
        acc[setting.key] = setting
        return acc
      }, {})
      
      setSettings(settingsMap)
      setAvailableModels(llmData.availableModels || [])
      setAvailableToolModels(llmData.availableToolModels || [])
      setDefaultModel(llmData.defaultModel || '')
      setDefaultToolModel(llmData.defaultToolModel || '')
      
      // Set global system prompt value
      if (settingsMap.globalSystemPrompt?.value) {
        setGlobalSystemPromptValue(settingsMap.globalSystemPrompt.value)
      }
      setHasUnsavedChanges(false)
    } catch (error) {
      console.error('Failed to load platform settings:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadSettings()
  }, [])

  // Update a specific setting
  const updateSetting = async (key: string, value: string) => {
    try {
      setSaving(true)
      await adminApi.updatePlatformSetting(key, value)
      await loadSettings() // Reload to get updated timestamps
    } catch (error) {
      console.error(`Failed to update setting ${key}:`, error)
    } finally {
      setSaving(false)
    }
  }

  // Update default model
  const updateDefaultModel = async (modelId: string) => {
    try {
      setSaving(true)
      await adminApi.updateDefaultLLMModel(modelId)
      setDefaultModel(modelId)
    } catch (error) {
      console.error('Failed to update default model:', error)
    } finally {
      setSaving(false)
    }
  }

  // Update default tool model
  const updateDefaultToolModel = async (modelId: string) => {
    try {
      setSaving(true)
      await adminApi.updateDefaultToolModel(modelId)
      setDefaultToolModel(modelId)
    } catch (error) {
      console.error('Failed to update default tool model:', error)
    } finally {
      setSaving(false)
    }
  }


  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Platform Settings</h1>
          <p className="text-muted-foreground">
            Configure platform-wide settings and preferences
          </p>
        </div>
      </div>

      {/* General Settings */}
      <Card>
        <CardHeader>
          <div className="flex items-center space-x-2">
            <Settings className="h-5 w-5 text-blue-600" />
            <CardTitle>General Settings</CardTitle>
          </div>
          <CardDescription>
            Basic platform configuration and behavior
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Invite Only Setting */}
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label className="text-base">Invite Only Mode</Label>
              <div className="text-sm text-muted-foreground">
                {settings.inviteOnly?.description}
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Badge variant={settings.inviteOnly?.value === 'true' ? 'default' : 'secondary'}>
                {settings.inviteOnly?.value === 'true' ? 'Enabled' : 'Disabled'}
              </Badge>
              <Button
                variant="outline"
                size="sm"
                onClick={() => updateSetting('inviteOnly', settings.inviteOnly?.value === 'true' ? 'false' : 'true')}
                disabled={saving}
              >
                {settings.inviteOnly?.value === 'true' ? 'Disable' : 'Enable'}
              </Button>
            </div>
          </div>

          {/* Default Invite Quota */}
          <div className="space-y-2">
            <Label htmlFor="defaultInviteQuota">Default Invite Quota</Label>
            <div className="text-sm text-muted-foreground mb-2">
              {settings.defaultInviteQuota?.description}
            </div>
            <div className="flex items-center space-x-2">
              <Input
                id="defaultInviteQuota"
                type="number"
                min="0"
                max="100"
                value={settings.defaultInviteQuota?.value || '5'}
                onChange={(e) => updateSetting('defaultInviteQuota', e.target.value)}
                className="w-32"
                disabled={saving}
              />
              <span className="text-sm text-muted-foreground">invites per new user</span>
            </div>
          </div>

          {/* OpenRouter Usage Tracking */}
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label className="text-base">OpenRouter Usage Tracking</Label>
              <div className="text-sm text-muted-foreground">
                {settings.openRouterIncludeUsage?.description || 'Enable OpenRouter usage tracking to get accurate cost data (adds ~200-300ms latency)'}
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Badge variant={settings.openRouterIncludeUsage?.value === 'true' ? 'default' : 'secondary'}>
                {settings.openRouterIncludeUsage?.value === 'true' ? 'Enabled' : 'Disabled'}
              </Badge>
              <Button
                variant="outline"
                size="sm"
                onClick={() => updateSetting('openRouterIncludeUsage', settings.openRouterIncludeUsage?.value === 'true' ? 'false' : 'true')}
                disabled={saving}
              >
                {settings.openRouterIncludeUsage?.value === 'true' ? 'Disable' : 'Enable'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* System Prompts */}
      <Card>
        <CardHeader>
          <div className="flex items-center space-x-2">
            <FileText className="h-5 w-5 text-orange-600" />
            <CardTitle>System Prompts</CardTitle>
          </div>
          <CardDescription>
            Configure global system prompts for agent behavior
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Global System Prompt */}
          <div className="space-y-2">
            <Label htmlFor="globalSystemPrompt">Global System Prompt</Label>
            <div className="text-sm text-muted-foreground mb-2">
              {settings.globalSystemPrompt?.description || 'Global system prompt used for all agents'}
            </div>
            <Textarea
              id="globalSystemPrompt"
              value={globalSystemPromptValue}
              onChange={(e) => {
                setGlobalSystemPromptValue(e.target.value)
                setHasUnsavedChanges(e.target.value !== (settings.globalSystemPrompt?.value || ''))
              }}
              className="min-h-[150px] font-mono text-sm"
              disabled={saving}
              placeholder="Enter the global system prompt..."
            />
            <div className="flex items-center justify-between">
              <div>
                {hasUnsavedChanges && (
                  <p className="text-sm text-amber-600">
                    You have unsaved changes
                  </p>
                )}
              </div>
              <Button
                size="sm"
                onClick={async () => {
                  await updateSetting('globalSystemPrompt', globalSystemPromptValue)
                  setHasUnsavedChanges(false)
                }}
                disabled={saving || !hasUnsavedChanges}
              >
                Save System Prompt
              </Button>
            </div>
            {settings.globalSystemPrompt?.updatedAt && (
              <p className="text-xs text-muted-foreground">
                Last updated: {new Date(settings.globalSystemPrompt.updatedAt).toLocaleString()}
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* LLM Configuration */}
      <Card>
        <CardHeader>
          <div className="flex items-center space-x-2">
            <Brain className="h-5 w-5 text-purple-600" />
            <CardTitle>LLM Configuration</CardTitle>
          </div>
          <CardDescription>
            Configure available language models and default selection
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Default Model Selection */}
          <div className="space-y-2">
            <Label htmlFor="defaultModel">Default Model</Label>
            <div className="text-sm text-muted-foreground mb-2">
              {settings.defaultModel?.description}
            </div>
            <Select value={defaultModel} onValueChange={updateDefaultModel}>
              <SelectTrigger>
                <SelectValue placeholder="Select default model" />
              </SelectTrigger>
              <SelectContent>
                {availableModels.map((model) => (
                  <SelectItem key={model.id} value={model.id}>
                    {model.name} ({model.provider})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Default Tool Model Selection */}
          <div className="space-y-2">
            <Label htmlFor="defaultToolModel">Default Tool Model</Label>
            <div className="text-sm text-muted-foreground mb-2">
              {settings.defaultToolModel?.description || 'Fast model used for tool calls and context building'}
            </div>
            <Select value={defaultToolModel} onValueChange={updateDefaultToolModel}>
              <SelectTrigger>
                <SelectValue placeholder="Select default tool model" />
              </SelectTrigger>
              <SelectContent>
                {availableToolModels.map((model) => (
                  <SelectItem key={model.id} value={model.id}>
                    {model.name} ({model.provider})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

        </CardContent>
      </Card>

      {/* Search Configuration */}
      <Card>
        <CardHeader>
          <div className="flex items-center space-x-2">
            <Search className="h-5 w-5 text-green-600" />
            <CardTitle>Search Configuration</CardTitle>
          </div>
          <CardDescription>
            Configure web search functionality and providers
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Search Enabled */}
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label className="text-base">Search Enabled</Label>
              <div className="text-sm text-muted-foreground">
                {settings['search.enabled']?.description || 'Enable or disable search functionality globally'}
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Badge variant={settings['search.enabled']?.value === 'true' ? 'default' : 'secondary'}>
                {settings['search.enabled']?.value === 'true' ? 'Enabled' : 'Disabled'}
              </Badge>
              <Button
                variant="outline"
                size="sm"
                onClick={() => updateSetting('search.enabled', settings['search.enabled']?.value === 'true' ? 'false' : 'true')}
                disabled={saving}
              >
                {settings['search.enabled']?.value === 'true' ? 'Disable' : 'Enable'}
              </Button>
            </div>
          </div>

          {/* Max Results */}
          <div className="space-y-2">
            <Label htmlFor="searchMaxResults">Max Search Results</Label>
            <div className="text-sm text-muted-foreground mb-2">
              {settings['search.maxResults']?.description || 'Default maximum number of search results'}
            </div>
            <div className="flex items-center space-x-2">
              <Input
                id="searchMaxResults"
                type="number"
                min="1"
                max="20"
                value={settings['search.maxResults']?.value || '5'}
                onChange={(e) => updateSetting('search.maxResults', e.target.value)}
                className="w-32"
                disabled={saving}
              />
              <span className="text-sm text-muted-foreground">results per search</span>
            </div>
          </div>

          {/* Daily Cost Budget */}
          <div className="space-y-2">
            <Label htmlFor="searchDailyBudget">Daily Cost Budget</Label>
            <div className="text-sm text-muted-foreground mb-2">
              {settings['search.costBudget.daily']?.description || 'Daily cost budget for search operations in USD'}
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-sm text-muted-foreground">$</span>
              <Input
                id="searchDailyBudget"
                type="number"
                min="0"
                step="0.01"
                value={settings['search.costBudget.daily']?.value || '10.00'}
                onChange={(e) => updateSetting('search.costBudget.daily', e.target.value)}
                className="w-32"
                disabled={saving}
              />
              <span className="text-sm text-muted-foreground">USD per day</span>
            </div>
          </div>

          {/* Per Conversation Cost Budget */}
          <div className="space-y-2">
            <Label htmlFor="searchConversationBudget">Per Conversation Budget</Label>
            <div className="text-sm text-muted-foreground mb-2">
              {settings['search.costBudget.perConversation']?.description || 'Maximum search cost per conversation in USD'}
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-sm text-muted-foreground">$</span>
              <Input
                id="searchConversationBudget"
                type="number"
                min="0"
                step="0.01"
                value={settings['search.costBudget.perConversation']?.value || '0.50'}
                onChange={(e) => updateSetting('search.costBudget.perConversation', e.target.value)}
                className="w-32"
                disabled={saving}
              />
              <span className="text-sm text-muted-foreground">USD per conversation</span>
            </div>
          </div>

          {/* Cache Settings */}
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label className="text-base">Search Result Caching</Label>
              <div className="text-sm text-muted-foreground">
                {settings['search.enableCaching']?.description || 'Enable caching of search results'}
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Badge variant={settings['search.enableCaching']?.value === 'true' ? 'default' : 'secondary'}>
                {settings['search.enableCaching']?.value === 'true' ? 'Enabled' : 'Disabled'}
              </Badge>
              <Button
                variant="outline"
                size="sm"
                onClick={() => updateSetting('search.enableCaching', settings['search.enableCaching']?.value === 'true' ? 'false' : 'true')}
                disabled={saving}
              >
                {settings['search.enableCaching']?.value === 'true' ? 'Disable' : 'Enable'}
              </Button>
            </div>
          </div>

          {/* Provider Status */}
          <div className="pt-4 border-t">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label className="text-base">Exa Search Provider</Label>
                <div className="text-sm text-muted-foreground">
                  {settings['search.providers.exa.enabled']?.value === 'true' 
                    ? 'Provider is enabled (API key configured in .env)'
                    : 'Provider is disabled'}
                </div>
              </div>
              <Badge variant={settings['search.providers.exa.enabled']?.value === 'true' ? 'default' : 'secondary'}>
                {settings['search.providers.exa.enabled']?.value === 'true' ? 'Active' : 'Inactive'}
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* System Information */}
      <Card>
        <CardHeader>
          <div className="flex items-center space-x-2">
            <Shield className="h-5 w-5 text-green-600" />
            <CardTitle>System Information</CardTitle>
          </div>
          <CardDescription>
            Current platform configuration and status
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-4">
              <div>
                <Label className="text-sm font-medium">Total Settings</Label>
                <p className="text-2xl font-bold">{Object.keys(settings).length}</p>
              </div>
              <div>
                <Label className="text-sm font-medium">Available Models</Label>
                <p className="text-2xl font-bold">{availableModels.length}</p>
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <Label className="text-sm font-medium">Default Model</Label>
                <p className="text-lg font-medium">
                  {availableModels.find(m => m.id === defaultModel)?.name || 'None selected'}
                </p>
              </div>
              <div>
                <Label className="text-sm font-medium">Default Tool Model</Label>
                <p className="text-lg font-medium">
                  {availableToolModels.find(m => m.id === defaultToolModel)?.name || 'None selected'}
                </p>
              </div>
              <div>
                <Label className="text-sm font-medium">Invite Mode</Label>
                <div className="flex items-center space-x-2">
                  {settings.inviteOnly?.value === 'true' ? (
                    <Badge variant="default">Invite Only</Badge>
                  ) : (
                    <Badge variant="secondary">Open Registration</Badge>
                  )}
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export default PlatformSettingsPage